import { getAvailableMonthEndDate, getDateString } from "@/util.js";

export const getSecuritiesDates = securities => {
    const firstPriceDatesOfSecurities = securities.filter(sec => !!sec.firstPriceDate).map(sec => new Date(`${sec.firstPriceDate}Z`).getTime());
    const lastPriceDatesOfSecurities = securities.filter(sec => !!sec.lastPriceDate).map(sec => new Date(`${sec.lastPriceDate}Z`).getTime());
    const sinceInceptionDatesOfSecurities = securities.filter(sec => !!sec.inceptionDate).map(sec => new Date(`${sec.inceptionDate}Z`).getTime());

    const minEarliestAvailableDate = new Date(Math.min(...firstPriceDatesOfSecurities)).toISOString().split(".")[0];
    const earliestCommonDate = new Date(Math.max(...firstPriceDatesOfSecurities)).toISOString().split(".")[0];
    const minSinceInceptionDate = new Date(Math.min(...sinceInceptionDatesOfSecurities)).toISOString().split(".")[0];
    let commonEndDate = new Date(Math.min(...lastPriceDatesOfSecurities)).toISOString().split(".")[0];
    //valid whether last price date is available month end date.
    commonEndDate = getAvailableMonthEndDate(commonEndDate);
    return {
        minEarliestAvailableDate,
        minSinceInceptionDate,
        earliestCommonDate,
        commonEndDate
    };
};

export const addMonths = (date, months) => {
    const newDate = new Date(date.getTime());
    const currentMonth = newDate.getMonth();
    const currentDay = newDate.getDate();
    const computedDate = new Date(newDate.getTime());
    computedDate.setMonth(currentMonth + months + 1, 0);
    const dayInComputedDate = computedDate.getDate();
    if (currentDay >= dayInComputedDate) {
        return computedDate;
    } else {
        newDate.setFullYear(computedDate.getFullYear(), computedDate.getMonth(), currentDay);
        return newDate;
    }
};

export const addYears = (date, years) => {
    return addMonths(date, years * 12);
};

export const endOfMonth = date => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

export const getTrailingNYearStartDate = (date, years) => {
    const startDate = addYears(date, -1 * years);
    return endOfMonth(startDate);
};

export const setTimePeriodForSecurities = ({
    securities,
    timePeriod,
    presetTimePeriod,
    defaultDateRange,
    globalAvailableDates,
    isApplyEarliestAvailable,
    isGlobalUpdate
}) => {
    const { earliestCommonDate, commonEndDate } = globalAvailableDates;
    const hasCommonEndDate = new Date(earliestCommonDate) < new Date(commonEndDate);
    if (!hasCommonEndDate) {
        return applyDateForNoCommonEndDate(securities);
    }
    if (isTimePeriodNotAvailable({ securities, timePeriod, commonEndDate }) && !isApplyEarliestAvailable) {
        return applyEarliestCommonForAll({ securities, earliestCommonDate, commonEndDate });
    }

    return securities.map(security => {
        let tp = timePeriod;
        let dateRange = defaultDateRange;
        if (!isGlobalUpdate && "timePeriod" in security) {
            tp = security.timePeriod;
            dateRange = {
                startDate: security.startDate,
                endDate: security.endDate
            };
        } else {
            tp = getNegotiationTimePeriod({
                security,
                timePeriod: tp,
                presetTimePeriod,
                commonEndDate
            });
        }
        return setTimePeriodForSecurity({
            security,
            timePeriod: tp,
            presetTimePeriod,
            defaultDateRange: dateRange,
            earliestCommonDate,
            commonEndDate
        });
    });
};

export const applyCustomTimePeriodDate = ({ security, startDate, endDate }) => {
    const isStartDateNotAvailable = new Date(startDate) < new Date(security.firstPriceDate);
    const securityMonthEndDate = getAvailableMonthEndDate(security.lastPriceDate);
    const isEndDateNotAvailable = new Date(endDate) > new Date(securityMonthEndDate);
    return {
        ...security,
        timePeriod: 0,
        startDate: isStartDateNotAvailable ? security.firstPriceDate : startDate,
        endDate: isEndDateNotAvailable ? securityMonthEndDate : endDate
    };
};

export const setTimePeriodForSecurity = ({ security, timePeriod, presetTimePeriod, defaultDateRange, earliestCommonDate, commonEndDate }) => {
    const timePeriodEnum = {
        0: "custom",
        5: "sinceInception",
        6: "earliestAvailable",
        7: "earliestCommon",
        99: "presetTimePeriod"
    };
    security = { ...security };
    delete security.presetTimePeriod;
    const setTimePeriodActions = {
        custom: () => setTimePeriodToCustom({ security, presetTimePeriod, defaultDateRange }),
        sinceInception: () => setTimePeriodToSinceInception(security, commonEndDate),
        earliestAvailable: () => setTimePeriodToEarliestAvailable(security, commonEndDate),
        earliestCommon: () => setTimePeriodToEarliestCommon({ security, earliestCommonDate, commonEndDate }),
        presetTimePeriod: () => setToPresetTimePeriod({ security, presetTimePeriod }),
        default: () =>
            setTimePeriodToTrailingNYear({
                security,
                timePeriod,
                commonEndDate
            })
    };
    const action = timePeriodEnum[timePeriod] || "default";
    return setTimePeriodActions[action]();
};

export const applyDateForNoCommonEndDate = securities => {
    return securities.map(security => {
        return {
            ...security,
            timePeriod: 0,
            startDate: security.firstPriceDate,
            endDate: getAvailableMonthEndDate(security.lastPriceDate)
        };
    });
};

export const setTimePeriodToCustom = ({ security, presetTimePeriod, defaultDateRange }) => {
    if (presetTimePeriod) {
        return {
            ...security,
            timePeriod: 0,
            startDate: security.firstPriceDate,
            endDate: presetTimePeriod.endDate
        };
    } else if (defaultDateRange) {
        const { startDate, endDate } = defaultDateRange;
        return applyCustomTimePeriodDate({
            security,
            startDate,
            endDate
        });
    }
    return {
        ...security,
        timePeriod: 0,
        startDate: security.firstPriceDate,
        endDate: getAvailableMonthEndDate(security.lastPriceDate)
    };
};

export const setTimePeriodToSinceInception = (security, commonEndDate) => {
    return {
        ...security,
        timePeriod: 5,
        startDate: security.inceptionDate,
        endDate: commonEndDate
    };
};

export const setTimePeriodToEarliestAvailable = (security, commonEndDate) => {
    return {
        ...security,
        timePeriod: 6,
        startDate: security.firstPriceDate,
        endDate: commonEndDate
    };
};

export const setTimePeriodToEarliestCommon = ({ security, earliestCommonDate, commonEndDate }) => {
    return {
        ...security,
        timePeriod: 7,
        startDate: earliestCommonDate,
        endDate: commonEndDate
    };
};

export const setTimePeriodToTrailingNYear = ({ security, timePeriod, commonEndDate }) => {
    const years = getTrailingYears(timePeriod);
    const endDate = new Date(commonEndDate);
    const startDate = getTrailingNYearStartDate(endDate, years);
    return {
        ...security,
        timePeriod,
        startDate: getDateString(startDate),
        endDate: commonEndDate
    };
};

export const isTimePeriodNotAvailable = ({ securities, timePeriod, commonEndDate }) => {
    const isTrailingNYear = getTrailingYears(timePeriod) !== -1;
    const isTrailingNYearNotAvailableForSecurities = securities.some(security => isTrailingNYearNotAvailable({ security, timePeriod, commonEndDate }));
    return isTrailingNYear && isTrailingNYearNotAvailableForSecurities;
};

export const isSinceInceptionDateNotAvailable = security => {
    return new Date(security.inceptionDate) < new Date(security.firstPriceDate);
};

export const getNegotiationTimePeriod = ({ security, timePeriod, presetTimePeriod, commonEndDate }) => {
    let tp = timePeriod;
    const isTrailingNYear = getTrailingYears(timePeriod) !== -1;
    const isTrailingYearsNotAvailable = isTrailingNYear && isTrailingNYearNotAvailable({ security, timePeriod, commonEndDate });
    const isSinceInceptionNotAvailable = tp === 5 && isSinceInceptionDateNotAvailable(security);
    const isPresetNotAvailable = tp === 99 && isPresetStartDateNotAvailable({ security, presetTimePeriod });
    if (isPresetNotAvailable) {
        tp = 0;
    } else if (isTrailingYearsNotAvailable || isSinceInceptionNotAvailable) {
        tp = 6;
    }
    return tp;
};

export const applyEarliestCommonForAll = ({ securities, earliestCommonDate, commonEndDate }) => {
    return securities.map(security => setTimePeriodToEarliestCommon({ security, earliestCommonDate, commonEndDate }));
};

export const isTrailingNYearNotAvailable = ({ security, timePeriod, commonEndDate }) => {
    const years = getTrailingYears(timePeriod);
    const endDate = new Date(commonEndDate);
    const earliestPriceAvailableDate = new Date(security.firstPriceDate);
    const startDate = getTrailingNYearStartDate(endDate, years);
    return startDate < earliestPriceAvailableDate;
};

export const getTrailingYears = timePeriod => {
    const yearMapping = {
        1: 1,
        2: 3,
        3: 5,
        4: 10,
        8: 15
    };
    return yearMapping[timePeriod] || -1;
};

export const isPortfolioTimePeriodNotAvailable = ({ portfolioList, timePeriod, presetTimePeriod, isApplyEarliestAvailable }) => {
    const globalAvailableDates = getGlobalTimePeriodAvailableDates({ portfolioList });
    const { earliestCommonDate: globalEarliestCommonDate, commonEndDate: globalCommonEndDate } = globalAvailableDates;
    const hasCommonEndDate = new Date(globalEarliestCommonDate) < new Date(globalCommonEndDate);
    if (!hasCommonEndDate) {
        return true;
    }
    const timePeriodEnum = {
        99: "presetTimePeriod"
    };
    const disabledActions = {
        presetTimePeriod: () =>
            portfolioList.some(portfolio => {
                const { earliestCommonDate } = getSecuritiesDates(portfolio.securities);
                return isPortfolioPresetTimePeriodNotAvailable({
                    securities: portfolio.securities,
                    presetTimePeriod,
                    earliestCommonDate,
                    commonEndDate: globalCommonEndDate,
                    isApplyEarliestAvailable
                });
            }),
        trailing: () =>
            portfolioList.some(portfolio => {
                const { minEarliestAvailableDate, earliestCommonDate } = getSecuritiesDates(portfolio.securities);
                return isPortfolioTrailingNYearNotAvailable({
                    timePeriod,
                    minEarliestAvailableDate,
                    earliestCommonDate,
                    commonEndDate: globalCommonEndDate,
                    isApplyEarliestAvailable
                });
            }),
        default: () => false
    };
    const isTrailingNYear = getTrailingYears(timePeriod) !== -1;
    const action = timePeriodEnum[timePeriod] || (isTrailingNYear ? "trailing" : "default");
    return disabledActions[action]();
};

export const isPortfolioTrailingNYearNotAvailable = ({ timePeriod, minEarliestAvailableDate, earliestCommonDate, commonEndDate, isApplyEarliestAvailable }) => {
    const years = getTrailingYears(timePeriod);
    const endDate = new Date(commonEndDate);
    const startDate = getTrailingNYearStartDate(endDate, years);
    if (isApplyEarliestAvailable) {
        return startDate < new Date(minEarliestAvailableDate);
    }
    return startDate < new Date(earliestCommonDate);
};

export const isSecurityTimePeriodNotAvailable = ({ security, timePeriod, presetTimePeriod, earliestCommonDate, commonEndDate }) => {
    const hasCommonEndDate = new Date(earliestCommonDate) < new Date(commonEndDate);
    if (!hasCommonEndDate && timePeriod !== 0) {
        return true;
    }
    const timePeriodEnum = {
        5: "sinceInception",
        99: "presetTimePeriod"
    };
    const disabledActions = {
        sinceInception: () => isSinceInceptionDateNotAvailable(security),
        presetTimePeriod: () => isPresetTimePeriodNotAvailable({ security, presetTimePeriod }),
        trailing: () => isTrailingNYearNotAvailable({ security, timePeriod, commonEndDate }),
        default: () => false
    };
    const isTrailingNYear = getTrailingYears(timePeriod) !== -1;
    const action = timePeriodEnum[timePeriod] || (isTrailingNYear ? "trailing" : "default");
    return disabledActions[action]();
};

export const getSecuritiesDateRangeByTimePeriod = (securities, timePeriod, globalAvailableDates) => {
    if (securities.length === 0) {
        return null;
    }
    const timePeriodEnum = {
        5: "sinceInception",
        6: "earliestAvailable",
        7: "earliestCommon"
    };
    const { minSinceInceptionDate, minEarliestAvailableDate, earliestCommonDate, commonEndDate } = globalAvailableDates;
    const hasCommonEndDate = new Date(earliestCommonDate) < new Date(commonEndDate);
    if (!hasCommonEndDate) {
        return null;
    }

    const dateRangesAction = {
        sinceInception: () => ({
            startDate: minSinceInceptionDate,
            endDate: commonEndDate
        }),
        earliestAvailable: () => ({
            startDate: minEarliestAvailableDate,
            endDate: commonEndDate
        }),
        earliestCommon: () => ({
            startDate: earliestCommonDate,
            endDate: commonEndDate
        }),
        default: () => {
            const years = getTrailingYears(timePeriod);
            const endDate = new Date(commonEndDate);
            const startDate = getTrailingNYearStartDate(endDate, years);
            return {
                startDate: getDateString(startDate),
                endDate: commonEndDate
            };
        }
    };
    const action = timePeriodEnum[timePeriod] || "default";
    return dateRangesAction[action]();
};

export const getCustomTimePeriodDateRange = securities => {
    if (securities.length === 0) {
        return null;
    }
    const hasValidTimePeriod = securities.every(security => typeof security.timePeriod !== "undefined" && security.startDate && security.endDate);
    if (!hasValidTimePeriod) {
        return null;
    } else {
        const hasSameStartDate = securities.every(security => security.startDate === securities[0].startDate);
        const hasSameEndDate = securities.every(security => security.endDate === securities[0].endDate);
        return {
            startDate: hasSameStartDate ? securities[0].startDate : "",
            endDate: hasSameEndDate ? securities[0].endDate : ""
        };
    }
};

export const setSecuritiesDatesByStartDate = ({ securities, startDate }) => {
    const strStartDate = getDateString(startDate);
    return securities.map(security => {
        const earliestAvailableDate = new Date(security.firstPriceDate);
        const newSecurity = { ...security };
        if (startDate < earliestAvailableDate) {
            newSecurity.startDate = newSecurity.firstPriceDate;
        } else if (startDate >= earliestAvailableDate && newSecurity.endDate && startDate > new Date(newSecurity.endDate)) {
            newSecurity.startDate = strStartDate;
            newSecurity.endDate = strStartDate;
        } else {
            newSecurity.startDate = strStartDate;
        }
        return newSecurity;
    });
};

export const setSecuritiesDatesByEndDate = ({ securities, endDate }) => {
    const strEndDate = getDateString(endDate);
    return securities.map(security => {
        const newSecurity = { ...security };
        if (newSecurity.startDate && endDate < new Date(newSecurity.startDate)) {
            newSecurity.startDate = strEndDate;
            newSecurity.endDate = strEndDate;
        } else {
            newSecurity.endDate = strEndDate;
        }
        return newSecurity;
    });
};

export const isPortfolioPresetTimePeriodNotAvailable = ({ securities, presetTimePeriod, earliestCommonDate, commonEndDate, isApplyEarliestAvailable }) => {
    const { endDate } = presetTimePeriod;
    if (new Date(commonEndDate) < new Date(endDate) || new Date(endDate) < new Date(earliestCommonDate)) {
        return true;
    }
    if (isApplyEarliestAvailable) {
        return securities.every(security => isPresetTimePeriodNotAvailable({ security, presetTimePeriod }));
    }
    return securities.some(security => isPresetTimePeriodNotAvailable({ security, presetTimePeriod }));
};

export const isPresetTimePeriodNotAvailable = ({ security, presetTimePeriod }) => {
    return isPresetStartDateNotAvailable({ security, presetTimePeriod }) || isPresetEndDateNotAvailable({ security, presetTimePeriod });
};

export const isPresetStartDateNotAvailable = ({ security, presetTimePeriod }) => {
    const { startDate } = presetTimePeriod;
    const { firstPriceDate } = security;
    return new Date(startDate) < new Date(firstPriceDate);
};

export const isPresetEndDateNotAvailable = ({ security, presetTimePeriod }) => {
    const { endDate } = presetTimePeriod;
    const { lastPriceDate } = security;
    const mostRecentAvailableDate = getAvailableMonthEndDate(lastPriceDate);
    return new Date(endDate) > new Date(mostRecentAvailableDate);
};

export const setToPresetTimePeriod = ({ security, presetTimePeriod }) => {
    const { startDate, endDate } = presetTimePeriod;
    return {
        ...security,
        timePeriod: 99,
        startDate: startDate,
        endDate: endDate,
        presetTimePeriod
    };
};

export const getAllSecurities = ({ portfolioList }) => {
    const allSecurities = [];
    portfolioList.forEach(portfolio => {
        portfolio.securities.forEach(security => {
            const isExist = allSecurities.findIndex(item => item.securityId === security.securityId) !== -1;
            if (!isExist) {
                allSecurities.push(security);
            }
        });
    });
    return allSecurities;
};

export const getGlobalTimePeriodAvailableDates = ({ portfolioList }) => {
    const allSecurities = getAllSecurities({ portfolioList });
    return getSecuritiesDates(allSecurities);
};

export const updatePortfolioByShares = async ({ portfolio, querySecurities, securityPriceMap, fetch }) => {
    let updatePortfolio = portfolio;
    const { allocateBy, currency } = portfolio;
    if (allocateBy === 2) {
        await getSecuritiesPrices({
            securities: querySecurities,
            currency,
            securityPriceMap,
            fetch
        });
        updatePortfolio = updatePortfolioByPrice({
            portfolio,
            securityPriceMap
        });
    }
    return updatePortfolio;
};

export const updatePortfolioByPrice = ({ portfolio, securityPriceMap }) => {
    let { securities } = portfolio;
    const totalAmount = securities.reduce((prev, security) => {
        const { securityId, startDate, investAmount, investShares } = security;
        const price = securityPriceMap[securityId] && securityPriceMap[securityId][startDate];
        const amount = price ? investShares * price : investAmount;
        return prev + amount;
    }, 0);
    securities = securities.map(security => {
        const { securityId, startDate, investAmount, investShares } = security;
        const price = securityPriceMap[securityId] && securityPriceMap[securityId][startDate];
        const amount = price ? investShares * price : investAmount;
        return {
            ...security,
            investAmount: amount,
            investPercent: (amount / totalAmount) * 100
        };
    });

    return {
        ...portfolio,
        securities,
        totalInvestAmount: totalAmount
    };
};

export const getSecuritiesPrices = async ({ securities, currency, securityPriceMap, fetch }) => {
    const data = securities
        .filter(security => {
            const { securityId, startDate } = security;
            return securityPriceMap[securityId] === undefined || securityPriceMap[securityId][startDate] === undefined;
        })
        .map(({ securityId, startDate }) => {
            return {
                securityId,
                date: startDate
            };
        });
    if (data.length === 0) {
        return;
    }
    const result = await getSecurityPriceByDate({
        securities: data,
        currency,
        fetch
    });
    result.forEach(({ securityId, prices }) => {
        let cachedSecurity = securityPriceMap[securityId];
        if (!cachedSecurity) {
            cachedSecurity = {};
        }
        prices.forEach(({ date, val }) => {
            cachedSecurity[date] = val;
        });
        securityPriceMap[securityId] = cachedSecurity;
    });
};

export const getSecurityPriceByDate = async ({ securities, currency, fetch }) => {
    return fetch({
        url: `/api/v1/security/price?destcurrency=${currency}`,
        method: "POST",
        body: JSON.stringify(securities)
    });
};
